import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Warehouse, WarehouseStockOverview } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";

const StockByWarehouse = () => {
  savePathToLocalStorage();

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [overview, setOverview] = useState<WarehouseStockOverview[]>([]);
  const [filterWarehouseId, setFilterWarehouseId] = useState<number | "">("");
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedWh, setExpandedWh] = useState<number | null>(null);

  useEffect(() => {
    mesApi.getSkladista().then(setWarehouses).catch(() => {});
  }, []);

  useEffect(() => {
    loadOverview();
  }, [filterWarehouseId]);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const data = await mesApi.getSkladistePregled(
        filterWarehouseId ? { warehouse_id: filterWarehouseId } : undefined
      );
      setOverview(data || []);
      if (!filterWarehouseId && data?.length) {
        setExpandedWh((prev) => prev ?? data[0].warehouse_id);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = (items: WarehouseStockOverview["items"]) => {
    if (!filterText.trim()) return items;
    const q = filterText.toLowerCase();
    return items.filter((s) => s.ingredient_name.toLowerCase().includes(q));
  };

  return (
    <div className="mes-page">
      <Header title="MES - Skladište" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2500} />

          <div className="iwt-header">
            <div>
              <h2>Stanje po skladištima</h2>
              <p className="iwt-role-hint">
                Pregled artikala — što je na stanju, koliko i u kojem skladištu
              </p>
            </div>
          </div>

          <div className="wh-pregled-toolbar">
            <select
              className="filter-select"
              value={filterWarehouseId}
              onChange={(e) =>
                setFilterWarehouseId(e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">Sva skladišta</option>
              {warehouses.map((s) => (
                <option key={s.warehouse_id} value={s.warehouse_id}>
                  {s.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Pretraži sirovinu..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-select"
              style={{ minWidth: 220 }}
            />
            <button type="button" className="btn-secondary" onClick={loadOverview} disabled={loading}>
              Osvježi
            </button>
          </div>

          {loading ? (
            <p className="iwt-empty">Učitavanje...</p>
          ) : overview.length === 0 ? (
            <p className="iwt-empty">Nema podataka o stanju</p>
          ) : (
            <div className="wh-pregled-list">
              {overview.map((wh) => {
                const items = filterItems(wh.items ?? []);
                const open = expandedWh === wh.warehouse_id;
                return (
                  <div key={wh.warehouse_id} className="mes-section wh-pregled-card">
                    <button
                      type="button"
                      className="wh-pregled-card-head"
                      onClick={() =>
                        setExpandedWh(open ? null : wh.warehouse_id)
                      }
                    >
                      <div>
                        <strong>{wh.name}</strong>
                        <span className="wh-pregled-meta">
                          {wh.code} · {wh.type} · {wh.total_items} artikala
                        </span>
                      </div>
                      <span>{open ? "▾" : "▸"}</span>
                    </button>
                    {open && (
                      <div className="mes-table-wrap">
                        <table className="mes-table iwt-lines-table">
                        <thead>
                          <tr>
                            <th>Sirovina</th>
                            <th style={{ textAlign: "right" }}>Ukupno kg</th>
                            <th>LOT-ovi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="iwt-empty">
                                Nema stavki
                              </td>
                            </tr>
                          ) : (
                            items.map((s) => (
                              <tr
                                key={s.ingredient_id}
                                className={s.low_stock_warning ? "iwt-line-warn" : ""}
                              >
                                <td>
                                  {s.ingredient_name}
                                  {s.low_stock_warning && (
                                    <span className="iwt-line-note">Niska zaliha</span>
                                  )}
                                </td>
                                <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                                  {s.ukupno_kg.toFixed(2)}
                                </td>
                                <td>
                                  {(s.lots ?? []).length === 0
                                    ? "—"
                                    : (s.lots ?? [])
                                        .map(
                                          (l) =>
                                            `${l.lot_number || l.lot_id}: ${l.kg.toFixed(1)} kg`
                                        )
                                        .join(", ")}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockByWarehouse;
